import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import PageSEO from '@/components/PageSEO';
import axios from 'axios';
import { Sun, Moon, Eye, EyeOff, ArrowRight, ArrowLeft, Loader2, Check, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  // A referral code arrives as /auth?ref=CODE and is redeemed when the
  // brand audit is submitted during onboarding.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) localStorage.setItem('omni-ref', ref);
  }, []);
  const { login, register } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setBusy(true);
    const result = mode === 'login'
      ? await login(email, password, remember)
      : await register(email, password, name);
    setBusy(false);
    if (result.success) {
      if (result.data?.onboarding_complete) navigate('/dashboard');
      else navigate('/onboarding');
    } else {
      setError(result.error);
    }
  }, [mode, email, password, name, remember, login, register, navigate]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) { setError('Enter your email address first'); return; }
    setError(''); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSuccess(data.message || 'Reset link sent! Check your email.');
      if (data.mock_reset_link) {
        console.log('Mock reset link:', data.mock_reset_link);
        setSuccess('Reset link sent! (Demo: check browser console for the link)');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send reset email');
    }
    setBusy(false);
  }, [email]);

  const seoTitle = mode === 'login' ? 'Sign In' : mode === 'register' ? 'Sign Up' : 'Forgot Password';

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${isDark ? 'bg-zinc-950' : 'bg-zinc-50'}`}
      style={{ fontFamily: "'Outfit', sans-serif" }}>
      <PageSEO title={seoTitle} description="Sign in to your OmniView marketing intelligence dashboard. Manage brands, channels, and media plans." keywords="OmniView login, marketing dashboard sign in" />
      <button data-testid="auth-theme-toggle" onClick={toggleTheme}
        className={`fixed top-4 right-4 w-10 h-10 rounded-lg flex items-center justify-center border ${isDark ? 'border-zinc-800 text-zinc-400' : 'border-zinc-200 text-zinc-500'}`}>
        {isDark ? <Sun size={16}/> : <Moon size={16}/>}
      </button>

      <div className={`w-full max-w-md rounded-2xl p-8 ${isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-white border border-zinc-200 shadow-xl shadow-zinc-200/50'}`}>
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>{'\u03A9'}</div>
            <span className="text-xl font-bold tracking-tight">OMNI<span style={{ color: '#00D4FF' }}>VIEW</span></span>
          </Link>
          {mode === 'forgot' ? (
            <>
              <h1 className="text-2xl font-bold mt-2">Reset Password</h1>
              <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                Enter your email and we'll send a reset link
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold mt-2">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
              <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {mode === 'login' ? 'Sign in to your dashboard' : '7-day free trial. No credit card required.'}
              </p>
            </>
          )}
        </div>

        {error && <div data-testid="auth-error" className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">{error}</div>}
        {success && <div data-testid="auth-success" className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 text-sm flex items-center gap-2"><Check size={14}/>{success}</div>}

        {mode === 'forgot' ? (
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium block mb-1 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>Email address</label>
              <div className={`flex items-center border rounded-lg overflow-hidden ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-zinc-300 bg-white'}`}>
                <Mail size={16} className={`ml-3 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}/>
                <input data-testid="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" className={`flex-1 px-3 py-3 text-sm outline-none ${isDark ? 'bg-zinc-800 text-white' : 'bg-white text-zinc-900'}`}/>
              </div>
            </div>
            <button data-testid="forgot-submit" onClick={handleForgotPassword} disabled={busy}
              className="w-full py-3 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
              {busy ? <Loader2 size={16} className="animate-spin"/> : <Mail size={16}/>} Send Reset Link
            </button>
            <button data-testid="back-to-login" onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
              className={`w-full py-2 text-sm font-medium flex items-center justify-center gap-2 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              <ArrowLeft size={14}/> Back to Sign In
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <label className={`text-sm font-medium block mb-1 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>Full name</label>
                  <input data-testid="auth-name" type="text" value={name} onChange={e => setName(e.target.value)}
                    placeholder="John Doe"
                    className={`w-full px-4 py-3 rounded-lg text-sm border outline-none transition-all focus:ring-2 focus:ring-blue-500/20 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500' : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'}`} />
                </div>
              )}
              <div>
                <label className={`text-sm font-medium block mb-1 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>Email</label>
                <input data-testid="auth-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" required
                  className={`w-full px-4 py-3 rounded-lg text-sm border outline-none transition-all focus:ring-2 focus:ring-blue-500/20 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500' : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'}`} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>Password</label>
                  {mode === 'login' && (
                    <button type="button" data-testid="forgot-password-link" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                      className="text-xs font-medium" style={{ color: '#00D4FF' }}>Forgot password?</button>
                  )}
                </div>
                <div className="relative">
                  <input data-testid="auth-password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder={mode === 'register' ? 'Min 6 characters' : 'Enter password'} required
                    className={`w-full px-4 py-3 pr-10 rounded-lg text-sm border outline-none transition-all focus:ring-2 focus:ring-blue-500/20 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500' : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'}`} />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
              {mode === 'login' && (
                <label className={`flex items-center gap-2 text-xs cursor-pointer select-none ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  <input data-testid="remember-device" type="checkbox" checked={remember}
                    onChange={e => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded accent-[#002FA7] cursor-pointer" />
                  Remember this device for 30 days
                </label>
              )}
              <button data-testid="auth-submit" type="submit" disabled={busy}
                className="w-full py-3 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
                {busy ? <Loader2 size={16} className="animate-spin"/> : null}
                {mode === 'login' ? 'Sign In' : 'Start Free Trial'} <ArrowRight size={16}/>
              </button>
            </form>

            <div className="mt-5 flex items-center gap-3">
              <div className={`flex-1 h-px ${isDark ? 'bg-zinc-800' : 'bg-zinc-200'}`}/>
              <span className={`text-xs ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>or</span>
              <div className={`flex-1 h-px ${isDark ? 'bg-zinc-800' : 'bg-zinc-200'}`}/>
            </div>

            <button data-testid="google-login-btn" onClick={() => {
              const redirectUrl = window.location.origin + '/dashboard';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
              className={`mt-4 w-full py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-3 border transition-all ${isDark ? 'border-zinc-700 bg-zinc-800 text-zinc-200 hover:bg-zinc-700' : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50'}`}>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
          </>
        )}

        <div className="mt-6 text-center">
          {mode !== 'forgot' && (
            <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button data-testid="auth-toggle" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setSuccess(''); }}
                className="font-semibold" style={{ color: '#00D4FF' }}>
                {mode === 'login' ? 'Sign up free' : 'Sign in'}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
